// FILE: TagsSection.logic.ts
// Purpose: Pure helpers behind Settings → Etiquetas — form state, handle
//          derivation, validation and grouping — kept out of the component so
//          they can be tested without rendering.
// Layer: Web (settings)
// Exports: `TagFormState`, `EMPTY_TAG_FORM`, `tagToFormState`, `handleFromLabel`,
//          `validateTagForm`, `buildTagCreateRequest`, `buildTagUpdateRequest`,
//          `groupTagsByNamespace`.

import {
  TAG_DESCRIPTION_MAX_LENGTH,
  TAG_LABEL_MAX_LENGTH,
  TAG_NAMESPACE_MAX_LENGTH,
  TAG_SLUG_MAX_LENGTH,
  type AdminTagCreateRequest,
  type AdminTagUpdateRequest,
  type Tag,
} from "@taskin-code/contracts";

export interface TagFormState {
  readonly namespace: string;
  readonly slug: string;
  readonly label: string;
  readonly description: string;
  readonly color: string;
}

export const EMPTY_TAG_FORM: TagFormState = {
  namespace: "",
  slug: "",
  label: "",
  description: "",
  color: "",
};

export const tagToFormState = (tag: Tag): TagFormState => ({
  namespace: tag.namespace,
  slug: tag.slug,
  label: tag.label,
  description: tag.description ?? "",
  color: tag.color ?? "",
});

/**
 * A handle proposed from the label, the way `slugFromTitle` does for memories.
 *
 * Accents are folded to their base letter rather than dropped, because these
 * labels are written in Spanish: `Diseño` has to become `diseno`, and simply
 * removing the character would produce `dise-o` — a handle nobody would guess
 * and nobody would type.
 */
export const handleFromLabel = (label: string): string =>
  label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TAG_SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");

const TOKEN_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The same rules the server applies, so the editor can say what is wrong before
 * a round trip. The server still checks — this is a courtesy, not the boundary.
 */
export function validateTagForm(form: TagFormState): string | null {
  const namespace = form.namespace.trim().toLowerCase();
  if (namespace.length > TAG_NAMESPACE_MAX_LENGTH) {
    return `El namespace no puede pasar de ${TAG_NAMESPACE_MAX_LENGTH} caracteres.`;
  }
  if (namespace !== "" && !TOKEN_PATTERN.test(namespace)) {
    return "El namespace tiene que ir en minúsculas, separado por guiones simples.";
  }

  const slug = form.slug.trim().toLowerCase();
  if (slug === "") return "Hace falta un identificador.";
  if (slug.length > TAG_SLUG_MAX_LENGTH) {
    return `El identificador no puede pasar de ${TAG_SLUG_MAX_LENGTH} caracteres.`;
  }
  if (!TOKEN_PATTERN.test(slug)) {
    return "El identificador tiene que ir en minúsculas, separado por guiones simples.";
  }

  if (form.label.trim() === "") return "Hace falta un nombre.";
  if (form.label.trim().length > TAG_LABEL_MAX_LENGTH) {
    return `El nombre no puede pasar de ${TAG_LABEL_MAX_LENGTH} caracteres.`;
  }
  if (form.description.trim().length > TAG_DESCRIPTION_MAX_LENGTH) {
    return `La descripción no puede pasar de ${TAG_DESCRIPTION_MAX_LENGTH} caracteres.`;
  }

  const color = form.color.trim().toLowerCase();
  if (color !== "" && !/^#[0-9a-f]{6}$/.test(color)) {
    return "El color tiene que ser un hex de seis dígitos, por ejemplo #3b82f6.";
  }

  return null;
}

export const buildTagCreateRequest = (form: TagFormState): AdminTagCreateRequest => ({
  namespace: form.namespace.trim().toLowerCase(),
  slug: form.slug.trim().toLowerCase(),
  label: form.label.trim(),
  description: form.description.trim() === "" ? null : form.description.trim(),
  color: form.color.trim() === "" ? null : form.color.trim().toLowerCase(),
});

/**
 * Namespace and slug are absent on purpose: they are the tag's identity, and the
 * server refuses to patch them. The editor locks both fields when editing, so a
 * request that carried them would only be a lie about what the form did.
 */
export const buildTagUpdateRequest = (form: TagFormState): AdminTagUpdateRequest => ({
  label: form.label.trim(),
  description: form.description.trim() === "" ? null : form.description.trim(),
  color: form.color.trim() === "" ? null : form.color.trim().toLowerCase(),
});

/**
 * Tags grouped for display, unqualified ones last.
 *
 * The order matters more than it looks: the namespace is what turns a flat list
 * of labels into a set of independent questions, so a screen that mixes them
 * together teaches exactly the wrong mental model of how matching works.
 */
export function groupTagsByNamespace(
  tags: ReadonlyArray<Tag>,
): ReadonlyArray<{ namespace: string; tags: ReadonlyArray<Tag> }> {
  const groups = new Map<string, Tag[]>();
  for (const tag of tags) {
    const existing = groups.get(tag.namespace);
    if (existing === undefined) groups.set(tag.namespace, [tag]);
    else existing.push(tag);
  }
  return [...groups.entries()]
    .map(([namespace, grouped]) => ({
      namespace,
      tags: grouped.toSorted((a, b) => a.slug.localeCompare(b.slug)),
    }))
    .toSorted((a, b) => {
      if (a.namespace === b.namespace) return 0;
      if (a.namespace === "") return 1;
      if (b.namespace === "") return -1;
      return a.namespace.localeCompare(b.namespace);
    });
}
